import React, { useEffect, useState, useRef } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import axios from "axios";
import "./LineGraph.css";
import { motion } from "framer-motion";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const LineGraph = ({ filters }) => {
  const [graphData, setGraphData] = useState({ labels: [], datasets: [] });
  const [range, setRange] = useState(0);
  const [toggleType, setToggleType] = useState("BR");
  const chartRef = useRef(null);
  const [fetchedData, setFetchedData] = useState(null);

  /* ================= FETCH DATA ================= */

  useEffect(() => {
    if (!filters || Object.keys(filters).length === 0) {
      setGraphData({
        labels: [],
        datasets: [],
      });
      return;
    }

    const fetchData = async () => {
      try {
        const response = await axios.get(
          "https://api-usersreport-insights.haqdarshak.com/api/weekly-applications",
          {
            params: filters,
            paramsSerializer: (params) => {
              const searchParams = new URLSearchParams();
              Object.keys(params).forEach((key) => {
                if (Array.isArray(params[key])) {
                  params[key].forEach((val) =>
                    searchParams.append(key, val)
                  );
                } else if (params[key]) {
                  searchParams.append(key, params[key]);
                }
              });
              return searchParams.toString();
            },
          }
        );

        setFetchedData(response.data);
      } catch (error) {
        console.error("Error fetching weekly applications data:", error);
      }
    };

    fetchData();
  }, [filters]);

  /* ================= PROCESS DATA ================= */

  useEffect(() => {
    if (!fetchedData) return;

    const { labels, datasets } = fetchedData;
    const prefix = toggleType;

    const getArray = (key) =>
      datasets.find((d) => d.label === `${prefix}_${key}`)?.data ||
      new Array(labels.length).fill(0);

    const di = getArray("DI");
    const dfl = getArray("DFL");
    const yc = getArray("YC");

    const updatedDatasets = [
      {
        label: `${prefix}_DI`,
        data: di.map((v) => Number(v) || 0),
        borderColor: "#4BC0C0",
        backgroundColor: "rgba(75,192,192,0.3)",
        fill: true,
      },
      {
        label: `${prefix}_DFL`,
        data: dfl.map((v) => Number(v) || 0),
        borderColor: "#FF6384",
        backgroundColor: "rgba(255,99,132,0.3)",
        fill: false,
      },
      {
        label: `${prefix}_YC`,
        data: yc.map((v) => Number(v) || 0),
        borderColor: "#9966FF",
        backgroundColor: "rgba(153,102,255,0.3)",
        fill: false,
      },
    ];

    setGraphData({
      labels,
      datasets: updatedDatasets.map((dataset) => ({
        ...dataset,
        pointBackgroundColor: "#fff",
        pointBorderColor: dataset.borderColor,
        tension: 0.4,
      })),
    });

    setRange(labels.length - 1);
  }, [toggleType, fetchedData]);

  /* ================= CHART OPTIONS ================= */

  const options = {
    responsive: true,
    plugins: {
      title: {
        display: true,
        text: "Weekly Applications Data",
        font: { size: 18, weight: "bold" },
      },
      tooltip: {
        mode: "index",
        intersect: false,
      },
      legend: {
        labels: { font: { size: 14 } },
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: "Weeks",
        },
      },
      y: {
        title: {
          display: true,
          text: "Applications",
        },
        beginAtZero: true,
      },
    },
  };

  /* ================= UI ================= */

  return (
    <motion.div
      className="line-graph-container"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="toggle-buttons2">
        {["Open", "DS", "BR"].map((type) => (
          <button
            key={type}
            className={toggleType === type ? "active" : ""}
            onClick={() => setToggleType(type)}
          >
            {type}
          </button>
        ))}
      </div>

      {graphData.labels.length > 0 ? (
        <>
          <Line
            ref={chartRef}
            data={{
              labels: graphData.labels.slice(0, range + 1),
              datasets: graphData.datasets.map((ds) => ({
                ...ds,
                data: ds.data.slice(0, range + 1),
              })),
            }}
            options={options}
          />

          <input
            type="range"
            min="0"
            max={graphData.labels.length - 1}
            value={range}
            onChange={(e) => setRange(Number(e.target.value))}
            className="slider"
          />
        </>
      ) : (
        <p className="no-data">
          No data available. Apply filters to view the graph.
        </p>
      )}
    </motion.div>
  );
};

export default LineGraph;
